import React, { useEffect, useState } from 'react';
import { Table } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Fuse from 'fuse.js';

import Search from '../assets/icons/search.svg';
import CrossIcon from '../assets/icons/cross-sign.svg';
import { MdDragIndicator } from 'react-icons/md';

const DragHandleContext = React.createContext();

const DraggableDataTable = ({
  removeContainerStyle,
  metadata,
  dataLoader,
  data,
  contentType,
  selectedRowKeys,
  setSelectedRowKeys,
  searchKeys,
  threshold = 0.28,
  onOrderChange,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [localData, setLocalData] = useState([]);

  const DragHandle = ({ rowKey }) => {
    const dragHandleProps = React.useContext(DragHandleContext);
    return (
      <div
        {...dragHandleProps}
        className="drag-handle cursor-move opacity-50 flex items-center justify-center hover:opacity-75 transition-opacity"
        data-row-key={rowKey}
      >
        <MdDragIndicator size={16} className="text-gray-500" />
      </div>
    );
  };

  useEffect(() => {
    dataLoader();
    setSearchValue('');
  }, [contentType]);

  useEffect(() => {
    setLocalData(dataParser(data.data));
  }, [data.data, searchValue]);

  const onSelectChange = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(localData);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalData(items);

    try {
      if (onOrderChange) {
        await onOrderChange(items);
      }
    } catch (error) {
      console.error('Failed to update order:', error);
      // Revert the change on error
      setLocalData(localData);
    }
  };

  const columns = [
    {
      title: '',
      dataIndex: 'dragHandle',
      width: 30,
      render: (_, record) => (
        <DragHandle rowKey={record.key} />
      ),
    },
    ...metadata
      .map((item) => ({
        title: item?.label,
        dataIndex: item?.name,
        ellipsis: item?.ellipsis,
        sorter: item?.sorter,
        sortDirections: item?.sortDirections,
        defaultSortOrder: item?.defaultSortOrder,
        align: item?.align,
        width: item?.width,
        render(text, record) {
          if (
            item?.name === 'event_sales_headline' ||
            item?.name === 'registration_url' ||
            item?.name === 'event_enrolled_headline'
          ) {
            return {
              children: (
                <div style={{ maxWidth: 150, whiteSpace: 'pre-wrap' }}>
                  {text}
                </div>
              ),
            };
          }
          return text;
        },
      }))
      .filter((i) => i.dataIndex !== 'key'),
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  const tokenizeString = (str) => {
    return str
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .map((word) => word.trim());
  };

  const dataParser = (data) => {
    let _data = [];

    if (_data.error) _data = [];
    for (let i = 0; i < data?.length; i++) {
      const originalItem = { ...data[i] };

      const processedFields = metadata.reduce((map, attr) => {
        if (attr?.name) map[attr?.name] = attr.parser(data[i]);
        return map;
      }, {});

      _data.push({
        ...originalItem,
        ...processedFields,
        key: `row-${i}`
      });
    }

    if (searchKeys && searchValue) {
      const normalizedSearchValue = tokenizeString(searchValue).join(" ");
      const returnData = new Fuse(_data, {
        keys: searchKeys,
        threshold,
      });

      const results = returnData.search(normalizedSearchValue);
      return results.map((d) => d.item);
    } else {
      return _data;
    }
  };

  const SearchBox = () => (
    <div className="border border-gray-200 h-8 rounded-full flex justify-center items-center">
      <img src={Search} className="w-4 h-4 ml-4" alt="" />
      <input
        id="search"
        name="search"
        type="text"
        value={searchValue}
        className="text-sm px-2 rounded-full focus:outline-none"
        onChange={(e) => setSearchValue(e.target.value)}
      />
      {searchValue && (
        <img
          src={CrossIcon}
          className="w-3 h-4 mr-4 opacity-30"
          alt="clear search input"
          onClick={() => setSearchValue('')}
        />
      )}
    </div>
  );

  const DraggableBody = ({ className, ...restProps }) => (
    <Droppable droppableId="table" type="ROW">
      {(provided) => (
        <tbody
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={className}
          {...restProps}
        >
          {restProps.children}
          {provided.placeholder}
        </tbody>
      )}
    </Droppable>
  );

  const DraggableRow = ({ children, ...restProps }) => {
    const index = localData.findIndex(x => x.key === restProps['data-row-key']);
    if (index === -1) return <tr {...restProps}>{children}</tr>;

    return (
      <Draggable key={restProps['data-row-key']} draggableId={restProps['data-row-key']} index={index}>
        {(provided, snapshot) => {
          const modifiedChildren = React.Children.map(children, (child, cellIndex) => {
            if (cellIndex === 0) {
              return (
                <DragHandleContext.Provider value={provided.dragHandleProps}>
                  {child}
                </DragHandleContext.Provider>
              );
            }

            return child;
          });

          return (
            <tr
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...restProps}
              style={{
                ...restProps.style,
                ...provided.draggableProps.style,
                backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'transparent',
              }}
            >
              {modifiedChildren}
            </tr>
          );
        }}
      </Draggable>
    );
  };

  return (
    <div
      className={
        removeContainerStyle ? '' : 'bg-white p-5 mt-5 ttnk-table-container'
      }
    >
      <div className="flex justify-between items-center">
        <label className="m-3 font-semibold text-gray-400">
          Showing {localData.length} result{localData.length > 1 ? 's' : ''}
        </label>
        {data.count > 0 && searchKeys && SearchBox()}
      </div>
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <Table
          className="ttnk-table"
          pagination={false}
          columns={columns}
          loading={data.loading}
          dataSource={localData}
          // rowSelection={rowSelection}
          components={{
            body: {
              wrapper: DraggableBody,
              row: DraggableRow,
            },
          }}
        />
      </DragDropContext>
    </div>
  );
};

export default DraggableDataTable;